import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../../helpers/common'
import { register } from '../../../../helpers/reducerRegistry'
import { CORPORATE_TYPE, FINANCIAL_TYPE } from '../constant'
import { convertTreeData, handleScreeningIndicatorResponse } from '../helper'
import {
  getCompanyStatus,
  getCompanyType,
  getICB,
  getLegalForm,
  getListingStatus,
  getLocation,
  getScreeningIndicators,
  getVSIC,
  getYearReports,
} from './thunk'

const initialState = {
  basicInformation: [],
  financials: {
    [CORPORATE_TYPE.CORPORATE]: {
      [FINANCIAL_TYPE.BALANCE_SHEET]: [],
      [FINANCIAL_TYPE.INCOME_STATEMENT]: [],
      [FINANCIAL_TYPE.CASHFLOW_STATEMENT]: [],
      [FINANCIAL_TYPE.RATIOS]: [],
    },
    [CORPORATE_TYPE.BANK]: {
      [FINANCIAL_TYPE.BALANCE_SHEET]: [],
      [FINANCIAL_TYPE.INCOME_STATEMENT]: [],
      [FINANCIAL_TYPE.CASHFLOW_STATEMENT]: [],
      [FINANCIAL_TYPE.RATIOS]: [],
    },
    [CORPORATE_TYPE.SECURITIES]: {
      [FINANCIAL_TYPE.BALANCE_SHEET]: [],
      [FINANCIAL_TYPE.INCOME_STATEMENT]: [],
      [FINANCIAL_TYPE.CASHFLOW_STATEMENT]: [],
      [FINANCIAL_TYPE.RATIOS]: [],
    },
    [CORPORATE_TYPE.INSURANCE]: {
      [FINANCIAL_TYPE.BALANCE_SHEET]: [],
      [FINANCIAL_TYPE.INCOME_STATEMENT]: [],
      [FINANCIAL_TYPE.CASHFLOW_STATEMENT]: [],
      [FINANCIAL_TYPE.RATIOS]: [],
    },
  },
  screeningIndicatorById: {},

  industryVSIC: {},
  industryVSICById: {},
  industryICB: {},
  industryICBById: {},

  yearReports: [],
  companyStatus: [],
  legalForm: [],
  companyType: [],
  location: {},
  locationById: {},
  listingStatus: [],

  loading: {
    indicator: true,
    vsic: true,
    icb: true,
    yearReports: true,
    companyStatus: true,
    legalForm: true,
    companyType: true,
    location: true,
    listingStatus: true,
  },

  alertPopup: {
    isShow: false,
    title: '',
    message: '',
    isI18n: true,
    translateProps: {},
  },

  saveCriteriaPopup: {
    isShow: false,
    lastPathname: null,
  },
}

export const slice = createSlice({
  name: 'tool/screening/common',
  initialState,
  reducers: {
    resetStore(state) {
      Object.keys(initialState).forEach((key) => {
        state[key] = initialState[key]
      })
    },
    changeAlertPopup: (state, action) => {
      state.alertPopup = { ...state.alertPopup, ...action.payload }
    },
    changeSaveCriteriaPopup: (state, action) => {
      state.saveCriteriaPopup = {
        ...state.saveCriteriaPopup,
        ...action.payload,
      }
    },
  },
  extraReducers: (builder) => {
    // ScreeningIndicators
    builder.addCase(getScreeningIndicators.pending, (state) => {
      state.loading.indicator = true
    })
    builder.addCase(getScreeningIndicators.fulfilled, (state, action) => {
      handleScreeningIndicatorResponse(state, action.payload)
    })
    builder.addCase(getScreeningIndicators.rejected, (state, action) => {
      state.loading.indicator = action.payload.loading
    })
    // VSIC
    builder.addCase(getVSIC.pending, (state) => {
      state.loading.vsic = true
    })
    builder.addCase(getVSIC.fulfilled, (state, action) => {
      state.industryVSIC = convertTreeData(action.payload)
      state.industryVSICById = keyBy(action.payload, 'id')
      state.loading.vsic = false
    })
    builder.addCase(getVSIC.rejected, (state, action) => {
      state.loading.vsic = action.payload.loading
    })
    // ICB
    builder.addCase(getICB.pending, (state) => {
      state.loading.icb = true
    })
    builder.addCase(getICB.fulfilled, (state, action) => {
      state.industryICB = convertTreeData(action.payload)
      state.industryICBById = keyBy(action.payload, 'id')
      state.loading.icb = false
    })
    builder.addCase(getICB.rejected, (state, action) => {
      state.loading.icb = action.payload.loading
    })
    // YearReports
    builder.addCase(getYearReports.pending, (state) => {
      state.loading.yearReports = true
    })
    builder.addCase(getYearReports.fulfilled, (state, action) => {
      state.yearReports = action.payload
      state.loading.yearReports = false
    })
    builder.addCase(getYearReports.rejected, (state, action) => {
      state.loading.yearReports = action.payload.loading
    })
    // Company Status
    builder.addCase(getCompanyStatus.pending, (state) => {
      state.loading.companyStatus = true
    })
    builder.addCase(getCompanyStatus.fulfilled, (state, action) => {
      state.companyStatus = action.payload.map((item) => ({
        id: item.activeStatusId,
        name: item.activeStatusName,
      }))
      state.loading.companyStatus = false
    })
    builder.addCase(getCompanyStatus.rejected, (state, action) => {
      state.loading.companyStatus = action.payload.loading
    })
    // Legal Form
    builder.addCase(getLegalForm.pending, (state) => {
      state.loading.legalForm = true
    })
    builder.addCase(getLegalForm.fulfilled, (state, action) => {
      state.legalForm = action.payload.map((item) => ({
        id: item.currentBusinessTypeId,
        parentId: item.parentCurrentBusinessTypeId,
        name: item.currentBusinessTypeName,
      }))
      state.loading.legalForm = false
    })
    builder.addCase(getLegalForm.rejected, (state, action) => {
      state.loading.legalForm = action.payload.loading
    })
    // CompanyType
    builder.addCase(getCompanyType.pending, (state) => {
      state.loading.companyType = true
    })
    builder.addCase(getCompanyType.fulfilled, (state, action) => {
      state.companyType = action.payload.map((item) => ({
        id: item.comTypeCode,
        name: item.comTypeName,
      }))
      state.loading.companyType = false
    })
    builder.addCase(getCompanyType.rejected, (state, action) => {
      state.loading.companyType = action.payload.loading
    })
    // Location
    builder.addCase(getLocation.pending, (state) => {
      state.loading.location = true
    })
    builder.addCase(getLocation.fulfilled, (state, action) => {
      state.location = convertTreeData(action.payload)
      state.locationById = keyBy(action.payload, 'id')
      state.loading.location = false
    })
    builder.addCase(getLocation.rejected, (state, action) => {
      state.loading.location = action.payload.loading
    })
    //getListingStatus
    builder.addCase(getListingStatus.pending, (state) => {
      state.loading.listingStatus = true
    })
    builder.addCase(getListingStatus.fulfilled, (state, action) => {
      state.listingStatus = action.payload.map((item) => ({
        id: item.listingStatus,
        name: item.listingStatusName,
      }))
      state.loading.listingStatus = false
    })
    builder.addCase(getListingStatus.rejected, (state, action) => {
      state.loading.listingStatus = action.payload.loading
    })
  },
})

export const selectData = (key) => (state) => state[slice.name][key]

export const selectBasicInformation = (state) =>
  state[slice.name].basicInformation

export const selectFinancials = (state) => state[slice.name].financials
export const selectScreeningIndicatorById = (id, attr) => (state) => {
  if (!id) return state[slice.name].screeningIndicatorById
  const data = state[slice.name].screeningIndicatorById[id]
  if (data) return attr ? data[attr] : data
}

export const selectIndustryVSIC = (state) => state[slice.name].industryVSIC
export const selectIndustryVSICById = (id, attr) => (state) => {
  if (!id) return state[slice.name].industryVSICById
  const data = state[slice.name].industryVSICById[id]
  if (data) return attr ? data[attr] : data
}
export const selectIndustryICB = (state) => state[slice.name].industryICB
export const selectIndustryICBById = (id, attr) => (state) => {
  if (!id) return state[slice.name].industryICBById
  const data = state[slice.name].industryICBById[id]
  if (data) return attr ? data[attr] : data
}

export const selectLocation = (state) => state[slice.name].location
export const selectLocationById = (id, attr) => (state) => {
  if (!id) return state[slice.name].locationById
  const data = state[slice.name].locationById[id]
  if (data) return attr ? data[attr] : data
}

export const selectYearReports = (state) => state[slice.name].yearReports
export const selectCompanyStatus = (state) => state[slice.name].companyStatus
export const selectLegalForm = (state) => state[slice.name].legalForm

export const selectLoading = (type) => (state) =>
  state[slice.name].loading[type]

export const selectIsLoadingSomeData = (state) =>
  Object.values(state[slice.name].loading).some(Boolean)

export const selectAlertPopup = (state) => state[slice.name].alertPopup

export const selectSaveCriteriaPopup = (state) =>
  state[slice.name].saveCriteriaPopup

export const { resetStore, changeAlertPopup, changeSaveCriteriaPopup } =
  slice.actions

register(slice.name, slice.reducer)
